const STATE_KEY = 'journeybot.guestAiJourney.v1';
const NOTICE_DISMISSED_KEY = 'journeybot.guestAiNoticeDismissed.v1';
const SESSION_ID_KEY = 'journeybot.guestAiSessionId.v1';

const storage = () => (typeof window === 'undefined' ? null : window.sessionStorage);

const id = (prefix) => {
  const suffix = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`;
};

const safeMessages = (value) => (
  Array.isArray(value)
    ? value
      .filter((message) => message && ['user', 'assistant'].includes(message.role) && typeof message.content === 'string')
      .slice(-40)
      .map((message, index) => ({
        id: typeof message.id === 'string' ? message.id : `restored-${index}`,
        role: message.role,
        content: message.content.slice(0, 8_000),
      }))
    : []
);

export const createGuestJourneyState = () => ({
  version: 1,
  attemptId: id('attempt'),
  messages: [],
  successfulAssistantResponses: 0,
  input: '',
  phase: 'chat',
  journey: null,
  generationError: '',
  updatedAt: new Date().toISOString(),
});

const sanitize = (candidate) => {
  if (!candidate || candidate.version !== 1 || typeof candidate.attemptId !== 'string') return null;
  const phase = candidate.phase === 'map' ? 'map' : 'chat';
  return {
    version: 1,
    attemptId: candidate.attemptId.slice(0, 128),
    messages: safeMessages(candidate.messages),
    successfulAssistantResponses: Math.max(0, Math.min(10, Number(candidate.successfulAssistantResponses) || 0)),
    input: typeof candidate.input === 'string' ? candidate.input.slice(0, 2_000) : '',
    phase,
    journey: phase === 'map' && candidate.journey && typeof candidate.journey === 'object' ? candidate.journey : null,
    boardData: phase === 'map' && candidate.boardData && typeof candidate.boardData === 'object' ? candidate.boardData : null,
    generationError: typeof candidate.generationError === 'string' ? candidate.generationError.slice(0, 500) : '',
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : new Date().toISOString(),
  };
};

export const guestJourneyToBoardData = (journey) => ({
  fromTheme: journey.startState,
  startState: journey.startState,
  context: journey.context,
  endTheme: journey.endState,
  endState: journey.endState,
  steps: journey.steps.map((step, stepIndex) => ({
    step_id: stepIndex,
    guestStepId: step.id,
    objective: step.title,
    objectiveDescription: step.description,
    items: step.actions.map((action, actionIndex) => ({
      keyID: action.id,
      value: action.title,
      description: action.description,
      status: 'active',
      relevance: 'unknown',
      user_note: '',
      order: actionIndex,
    })),
  })),
});

export const loadGuestJourneyState = () => {
  try {
    const value = storage()?.getItem(STATE_KEY);
    return value ? sanitize(JSON.parse(value)) : null;
  } catch {
    return null;
  }
};

export const saveGuestJourneyState = (state) => {
  const next = sanitize({ ...state, version: 1, updatedAt: new Date().toISOString() });
  if (!next) throw new Error('Guest journey state is invalid.');
  storage()?.setItem(STATE_KEY, JSON.stringify(next));
  return next;
};

export const clearGuestJourneyState = () => storage()?.removeItem(STATE_KEY);

export const getGuestSessionId = () => {
  let value = storage()?.getItem(SESSION_ID_KEY);
  if (!/^[A-Za-z0-9_-]{16,128}$/.test(value || '')) {
    value = id('guest');
    storage()?.setItem(SESSION_ID_KEY, value);
  }
  return value;
};

export const isGuestAiNoticeDismissed = () => storage()?.getItem(NOTICE_DISMISSED_KEY) === 'true';
export const dismissGuestAiNotice = () => storage()?.setItem(NOTICE_DISMISSED_KEY, 'true');

export const guestJourneySessionKeys = Object.freeze({
  state: STATE_KEY,
  noticeDismissed: NOTICE_DISMISSED_KEY,
  sessionId: SESSION_ID_KEY,
});
